import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, LogIn, MoreHorizontal, Shield, Sparkles } from 'lucide-react';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';
import { HeaderUserMenu } from './HeaderUserMenu';
import { UserAvatar } from './UserAvatar';
import { useAuth } from '../lib/AuthContext';

const GUEST_CORE = [
  { to: '/', label: 'Home' },
  { to: '/prijzen', label: 'Pakketten' },
  { to: '/demo', label: 'Demo' },
  { to: '/blog', label: 'Blog' },
  { to: '/reviews', label: 'Reviews' },
] as const;

/** Ingelogd: korte hoofdbalk; Reviews, intro en nieuw dossier altijd onder “Meer” op tablet/desktop (ruimte voor Platformbeheer / Facturatie). */
const AUTH_CORE = [
  { to: '/', label: 'Home' },
  { to: '/prijzen', label: 'Pakketten' },
  { to: '/blog', label: 'Blog' },
  { to: '/dashboard', label: 'Dashboard' },
] as const;

const AUTH_SECONDARY = [
  { to: '/reviews', label: 'Reviews' },
  { to: '/demo', label: 'Product-demo' },
  { to: '/onboarding', label: 'Introductie' },
  { to: '/projects/new', label: 'Nieuw dossier' },
] as const;

function NavLink({ to, label, onClick, className = '' }: { to: string; label: string; onClick?: () => void; className?: string }) {
  const location = useLocation();
  const active = to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);
  return (
    <Link
      to={to}
      onClick={onClick}
      className={`whitespace-nowrap rounded-md px-2 py-1.5 text-sm transition hover:bg-surface hover:text-primary ${
        active ? 'font-medium text-primary' : 'text-text-muted'
      } ${className}`}
    >
      {label}
    </Link>
  );
}

function Dropdown({
  label,
  icon: Icon,
  children,
}: {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onDoc);
    return () => document.removeEventListener('click', onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-sm text-text-muted transition hover:bg-surface hover:text-text"
        aria-expanded={open}
      >
        {Icon && <Icon className="h-4 w-4" />}
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute left-0 z-[60] mt-1 min-w-[11rem] rounded-xl border border-border bg-surface-card py-1 shadow-lg xl:left-auto xl:right-0">
          {children}
        </div>
      )}
    </div>
  );
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [avatarBump, setAvatarBump] = useState(0);
  const location = useLocation();
  const { isAccountManager, isSuperAdmin, user, logout } = useAuth();
  const isUserAccount = user?.session_kind === 'user';
  const core = isUserAccount ? AUTH_CORE : GUEST_CORE;

  useEffect(() => {
    const onAvatar = () => setAvatarBump((n) => n + 1);
    window.addEventListener('calq-avatar-updated', onAvatar);
    return () => window.removeEventListener('calq-avatar-updated', onAvatar);
  }, []);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-surface-card/95 text-text shadow-sm backdrop-blur-sm">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-3 py-2.5 sm:px-4 lg:px-6">
        <Link
          to="/"
          className="flex min-w-0 shrink-0 items-center font-semibold text-text transition-opacity hover:opacity-90 hover:no-underline"
        >
          <Logo variant="header" size={34} />
        </Link>

        {/* Tablet + desktop */}
        <nav className="hidden min-w-0 flex-1 items-center justify-end gap-0.5 md:flex lg:gap-1" aria-label="Hoofdnavigatie">
          {core.map(({ to, label }) => (
            <NavLink key={to} to={to} label={label} />
          ))}

          {isUserAccount && (
            <Dropdown label="Meer" icon={MoreHorizontal}>
              {AUTH_SECONDARY.map(({ to, label }) => (
                <Link key={to} to={to} className="block px-3 py-2 text-sm text-text hover:bg-surface">
                  {label}
                </Link>
              ))}
            </Dropdown>
          )}

          {(isSuperAdmin || isAccountManager) && (
            <>
              {isSuperAdmin && (
                <Link
                  to="/admin"
                  className={`hidden whitespace-nowrap rounded-md px-2 py-1.5 text-sm xl:inline-flex ${
                    location.pathname.startsWith('/admin')
                      ? 'font-medium text-primary'
                      : 'text-text-muted hover:bg-surface hover:text-primary'
                  }`}
                >
                  Platformbeheer
                </Link>
              )}
              {isAccountManager && (
                <>
                  <Link
                    to="/account/billing"
                    className={`hidden whitespace-nowrap rounded-md px-2 py-1.5 text-sm xl:inline-flex ${
                      location.pathname.startsWith('/account/billing')
                        ? 'font-medium text-primary'
                        : 'text-text-muted hover:bg-surface hover:text-primary'
                    }`}
                  >
                    Facturatie
                  </Link>
                  <Link
                    to="/account/labor-rates"
                    className={`hidden whitespace-nowrap rounded-md px-2 py-1.5 text-sm xl:inline-flex ${
                      location.pathname.startsWith('/account/labor-rates')
                        ? 'font-medium text-primary'
                        : 'text-text-muted hover:bg-surface hover:text-primary'
                    }`}
                  >
                    Uurtarieven
                  </Link>
                </>
              )}
              <div className="xl:hidden">
                <Dropdown label="Beheer" icon={Shield}>
                  {isSuperAdmin && (
                    <Link to="/admin" className="block px-3 py-2 text-sm hover:bg-surface">
                      Platformbeheer
                    </Link>
                  )}
                  {isAccountManager && (
                    <>
                      <Link to="/account/billing" className="block px-3 py-2 text-sm hover:bg-surface">
                        Facturatie
                      </Link>
                      <Link to="/account/labor-rates" className="block px-3 py-2 text-sm hover:bg-surface">
                        Uurtarieven
                      </Link>
                    </>
                  )}
                </Dropdown>
              </div>
            </>
          )}

          {!isUserAccount && (
            <Link
              to="/login?returnTo=%2Fprojects%2Fnew"
              className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-alt"
            >
              <Sparkles className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Start</span>
            </Link>
          )}

          <div className="ml-1 flex shrink-0 items-center border-l border-border/60 pl-2">
            <ThemeToggle />
          </div>

          {isUserAccount && (
            <div className="ml-1 shrink-0 border-l border-border/60 pl-2">
              <HeaderUserMenu avatarVersion={avatarBump} />
            </div>
          )}

          {!isUserAccount && (
            <Link
              to="/login"
              className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm font-medium hover:border-primary hover:text-primary"
            >
              <LogIn className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Inloggen</span>
            </Link>
          )}
        </nav>

        <div className="flex items-center gap-1 md:hidden">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="rounded-lg p-2 text-text-muted hover:bg-surface hover:text-text"
            aria-label={mobileOpen ? 'Menu sluiten' : 'Menu openen'}
          >
            <span className="sr-only">Menu</span>
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav
          className="border-t border-border bg-surface-card px-3 py-3 md:hidden"
          role="navigation"
          aria-label="Mobiel menu"
        >
          <div className="flex flex-col gap-0.5">
            {core.map(({ to, label }) => (
              <NavLink key={to} to={to} label={label} onClick={() => setMobileOpen(false)} />
            ))}
            {isUserAccount &&
              AUTH_SECONDARY.map(({ to, label }) => (
                <NavLink key={to} to={to} label={label} onClick={() => setMobileOpen(false)} />
              ))}
            {isSuperAdmin && (
              <NavLink to="/admin" label="Platformbeheer" onClick={() => setMobileOpen(false)} />
            )}
            {isAccountManager && (
              <>
                <NavLink to="/account/billing" label="Facturatie" onClick={() => setMobileOpen(false)} />
                <NavLink to="/account/labor-rates" label="Uurtarieven" onClick={() => setMobileOpen(false)} />
              </>
            )}
            {!isUserAccount && (
              <Link
                to="/login?returnTo=%2Fprojects%2Fnew"
                onClick={() => setMobileOpen(false)}
                className="mt-2 rounded-lg bg-primary px-3 py-2 text-center text-sm font-medium text-white"
              >
                Start (account)
              </Link>
            )}
            {isUserAccount && (
              <div className="mt-3 border-t border-border pt-3">
                <div className="mb-3 flex items-center gap-3 rounded-lg px-2 py-2">
                  <UserAvatar
                    displayName={user?.display_name}
                    email={user?.email}
                    hasAvatar={user?.has_avatar}
                    size={40}
                    imageVersion={avatarBump}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-text">
                      {user?.display_name?.trim() || user?.email?.split('@')[0] || 'Account'}
                    </p>
                    {user?.email && <p className="truncate text-xs text-text-muted">{user.email}</p>}
                  </div>
                </div>
                <Link
                  to="/account"
                  onClick={() => setMobileOpen(false)}
                  className="mb-2 flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-primary hover:bg-surface"
                >
                  Mijn account
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    setMobileOpen(false);
                    void logout();
                  }}
                  className="w-full rounded-lg border border-border px-3 py-2 text-left text-sm"
                >
                  Uitloggen
                </button>
              </div>
            )}
            {!isUserAccount && (
              <Link
                to="/login"
                onClick={() => setMobileOpen(false)}
                className="mt-2 rounded-lg border border-border px-3 py-2 text-center text-sm"
              >
                Inloggen
              </Link>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
